import { hideNotification } from '../../notifications/actions';
import { isPrejoinPageVisible } from '../../prejoin/functions';
import { setAudioSettings } from '../../settings/actions.web';
import { getAvailableDevices } from '../devices/actions.web';
import { SET_AUDIO_MUTED } from '../media/actionTypes';
import { gumPending, setScreenshareMuted } from '../media/actions';
import { MEDIA_TYPE, VIDEO_TYPE } from '../media/constants';
import { IGUMPendingState } from '../media/types';
import MiddlewareRegistry from '../redux/MiddlewareRegistry';
import { TRACK_ADDED, TRACK_MUTE_UNMUTE_FAILED, TRACK_NO_DATA_FROM_SOURCE, TRACK_REMOVED, TRACK_STOPPED, TRACK_UPDATED } from './actionTypes';
import { createLocalTracksA, showNoDataFromSourceError, toggleScreensharing, trackMuteUnmuteFailed, trackNoDataFromSourceNotificationInfoChanged } from './actions.web';
import { getLocalTrack, getLocalVietMeetAudioTrackSettings, getTrackByVietMeetTrack, isUserInteractionRequiredForUnmute, logTracksForParticipant, setTrackMuted } from './functions.web';
import './middleware.any';
import './subscriber.web';
/**
 * Middleware that captures LIB_DID_DISPOSE and LIB_DID_INIT actions and,
 * respectively, creates/destroys local media tracks. Also listens to
 * media-related actions and performs corresponding operations with tracks.
 *
 * @param {Store} store - The redux store.
 * @returns {Function}
 */
MiddlewareRegistry.register(store => next => action => {
    switch (action.type) {
        case TRACK_ADDED: {
            const { local, vietmeetTrack } = action.track;
            // The devices list needs to be refreshed when no initial video permissions
            // were granted and a local video track is added by umuting the video.
            if (local) {
                store.dispatch(getAvailableDevices());
                break;
            }
            const result = next(action);
            const participantId = action.track?.participantId;
            if (participantId) {
                logTracksForParticipant(store.getState()['features/base/tracks'], participantId, 'Track added');
                // Fire participantMuted event for initial state of remote tracks
                if (typeof action.track?.muted !== 'undefined' && vietmeetTrack) {
                    const isVideoTrack = vietmeetTrack.getType() !== MEDIA_TYPE.AUDIO;
                    const mediaType = isVideoTrack
                        ? (vietmeetTrack.getVideoType() === VIDEO_TYPE.DESKTOP ? 'desktop' : 'video')
                        : 'audio';
                    APP.API.notifyParticipantMuted(participantId, action.track.muted, mediaType);
                }
            }
            return result;
        }
        case TRACK_NO_DATA_FROM_SOURCE: {
            const result = next(action);
            _handleNoDataFromSourceErrors(store, action);
            return result;
        }
        case TRACK_REMOVED: {
            _removeNoDataFromSourceNotification(store, action.track);
            const result = next(action);
            const participantId = action.track?.vietmeetTrack?.getParticipantId();
            if (participantId && !action.track?.vietmeetTrack?.isLocal()) {
                logTracksForParticipant(store.getState()['features/base/tracks'], participantId, 'Track removed');
            }
            return result;
        }
        case TRACK_MUTE_UNMUTE_FAILED: {
            const { vietmeetTrack } = action.track;
            const muted = action.wasMuted;
            const isVideoTrack = vietmeetTrack.getType() !== MEDIA_TYPE.AUDIO;
            if (isVideoTrack && vietmeetTrack.getVideoType() === VIDEO_TYPE.DESKTOP) {
                store.dispatch(setScreenshareMuted(!muted));
            }
            else if (isVideoTrack) {
                APP.conference.setVideoMuteStatus();
            }
            else {
                APP.conference.updateAudioIconEnabled();
            }
            break;
        }
        case TRACK_STOPPED: {
            const { vietmeetTrack } = action.track;
            if (vietmeetTrack.getVideoType() === VIDEO_TYPE.DESKTOP) {
                store.dispatch(toggleScreensharing(false));
            }
            break;
        }
        case TRACK_UPDATED: {
            // TODO Remove the following calls to APP.UI once components interested
            // in track mute changes are moved into React and/or redux.
            const { vietmeetTrack } = action.track;
            const participantID = vietmeetTrack.getParticipantId();
            const isVideoTrack = vietmeetTrack.type !== MEDIA_TYPE.AUDIO;
            const local = vietmeetTrack.isLocal();
            // Get old muted state BEFORE updating
            const tracks = store.getState()['features/base/tracks'];
            const oldTrack = tracks.find((t) => t.vietmeetTrack === vietmeetTrack);
            const oldMutedState = oldTrack?.muted;
            const result = next(action);
            const state = store.getState();
            if (isPrejoinPageVisible(state)) {
                return result;
            }
            if (isVideoTrack) {
                if (local && !(vietmeetTrack.getVideoType() === VIDEO_TYPE.DESKTOP)) {
                    APP.conference.setVideoMuteStatus();
                }
                else if (!local) {
                    APP.UI.setVideoMuted(participantID);
                }
            }
            else if (local) {
                APP.conference.updateAudioIconEnabled();
            }
            if (typeof action.track?.muted !== 'undefined' && participantID && !local) {
                logTracksForParticipant(store.getState()['features/base/tracks'], participantID, 'Track updated');
                // Fire participantMuted event only if muted state actually changed
                if (oldMutedState !== action.track.muted) {
                    const mediaType = isVideoTrack
                        ? (vietmeetTrack.getVideoType() === VIDEO_TYPE.DESKTOP ? 'desktop' : 'video')
                        : 'audio';
                    APP.API.notifyParticipantMuted(participantID, action.track.muted, mediaType);
                }
            }
            return result;
        }
        case SET_AUDIO_MUTED: {
            if (!action.muted
                && isUserInteractionRequiredForUnmute(store.getState())) {
                return;
            }
            if (action.muted) {
                const localAudioTrack = getLocalTrack(store.getState()['features/base/tracks'], MEDIA_TYPE.AUDIO);
                if (localAudioTrack) {
                    const { vietmeetTrack, noDataFromSourceNotificationInfo = {} } = localAudioTrack;
                    if (noDataFromSourceNotificationInfo.timeout) {
                        clearTimeout(noDataFromSourceNotificationInfo.timeout);
                        store.dispatch(trackNoDataFromSourceNotificationInfoChanged(vietmeetTrack, undefined));
                    }
                    _removeNoDataFromSourceNotification(store, localAudioTrack);
                }
            }
            _setMuted(store, action);
            break;
        }
    }
    return next(action);
});
/**
 * Handles no data from source errors.
 *
 * @param {Store} store - The redux store in which the specified action is
 * dispatched.
 * @param {Action} action - The redux action dispatched in the specified store.
 * @private
 * @returns {void}
 */
function _handleNoDataFromSourceErrors(store, action) {
    const { getState, dispatch } = store;
    const track = getTrackByVietMeetTrack(getState()['features/base/tracks'], action.track.vietmeetTrack);
    if (!track?.local) {
        return;
    }
    const { vietmeetTrack, noDataFromSourceNotificationInfo = {} } = track;
    if (track.isReceivingData || track.muted) {
        if (noDataFromSourceNotificationInfo.timeout) {
            clearTimeout(noDataFromSourceNotificationInfo.timeout);
            dispatch(trackNoDataFromSourceNotificationInfoChanged(vietmeetTrack, undefined));
        }
        _removeNoDataFromSourceNotification(store, action.track);
    }
    else {
        if (noDataFromSourceNotificationInfo.timeout || noDataFromSourceNotificationInfo.uid) {
            return;
        }
        const timeout = setTimeout(() => dispatch(showNoDataFromSourceError(vietmeetTrack)), 5000);
        dispatch(trackNoDataFromSourceNotificationInfoChanged(vietmeetTrack, { timeout }));
    }
}
/**
 * Removes the no data from source notification associated with the VietMeetTrack if displayed.
 *
 * @param {Store} store - The redux store.
 * @param {Track} track - The redux action dispatched in the specified store.
 * @returns {void}
 */
function _removeNoDataFromSourceNotification({ getState, dispatch }, track) {
    const t = getTrackByVietMeetTrack(getState()['features/base/tracks'], track.vietmeetTrack);
    const { vietmeetTrack, noDataFromSourceNotificationInfo = {} } = t || {};
    if (noDataFromSourceNotificationInfo?.uid) {
        dispatch(hideNotification(noDataFromSourceNotificationInfo.uid));
        dispatch(trackNoDataFromSourceNotificationInfoChanged(vietmeetTrack, undefined));
    }
}
/**
 * Mutes or unmutes a local track with a specific media type.
 *
 * @param {Store} store - The redux store in which the specified action is
 * dispatched.
 * @param {Action} action - The redux action dispatched in the specified store.
 * @private
 * @returns {void}
 */
function _setMuted(store, { ensureTrack, muted }) {
    const { dispatch, getState } = store;
    const state = getState();
    const localTrack = getLocalTrack(state['features/base/tracks'], MEDIA_TYPE.AUDIO, /* includePending */ true);
    if (localTrack) {
        // The `vietmeetTrack` property will have a value only for a localTrack for which `getUserMedia` has already
        // completed. If there's no `vietmeetTrack`, then the `muted` state will be applied once the `vietmeetTrack` is
        // created.
        const { vietmeetTrack } = localTrack;
        if (vietmeetTrack) {
            setTrackMuted(vietmeetTrack, muted, state, dispatch)
                .catch(() => {
                dispatch(trackMuteUnmuteFailed(localTrack, muted));
            });
        }
    }
    else if (!muted && ensureTrack) {
        // TODO(saghul): reconcile these 2 types.
        dispatch(gumPending([MEDIA_TYPE.AUDIO], IGUMPendingState.PENDING_UNMUTE));
        const createTrackOptions = {
            devices: [MEDIA_TYPE.AUDIO],
        };
        dispatch(createLocalTracksA(createTrackOptions)).then(() => {
            dispatch(gumPending([MEDIA_TYPE.AUDIO], IGUMPendingState.NONE));
            const updatedSettings = getLocalVietMeetAudioTrackSettings(getState());
            dispatch(setAudioSettings(updatedSettings));
        });
    }
}
